import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/app/auth-context-state";
import { useCall } from "@/app/call-context-state";
import cls from "./CallOverlay.module.scss";
import adminAvatar from "@/assets/UI/admin.webp";
import unmuteIcon from "@/assets/UI/unmute.webp";
import muteIcon from "@/assets/UI/mute.webp";
import cameraOnIcon from "@/assets/UI/cameraOn.webp";
import cameraOffIcon from "@/assets/UI/cameraOff.webp";
import switchCameraIcon from "@/assets/UI/switchCamera.webp";

const portalRoot = typeof document !== "undefined" ? document.body : null;

const getCallerLabel = (incomingCall) => {
  const caller = incomingCall?.caller || {};
  return caller?.username || "Unknown";
};

const getCallerAvatar = (incomingCall) => {
  const caller = incomingCall?.caller || {};
  if (caller?.role === "admin") {
    return adminAvatar;
  }
  return caller?.avatarUrl || "";
};

const getParticipantLabel = (participant, fallback) =>
  participant?.username || fallback;

const getParticipantAvatar = (participant) => {
  if (participant?.role === "admin") {
    return adminAvatar;
  }
  return participant?.avatarUrl || "";
};

export default function CallOverlay() {
  const { user } = useAuth();
  const {
    incomingCall,
    activeCall,
    callStatus,
    isConnecting,
    localTracks,
    remoteTracks,
    isMuted,
    isCameraOff,
    answerCall,
    declineCall,
    endCall,
    toggleMute,
    toggleCamera,
    switchCamera,
  } = useCall();

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const cardRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (localTracks.video && localVideoRef.current) {
      localTracks.video.play(localVideoRef.current);
    }
  }, [localTracks.video]);

  useEffect(() => {
    if (remoteTracks.video && remoteVideoRef.current) {
      remoteTracks.video.play(remoteVideoRef.current);
    }
  }, [remoteTracks.video]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      if (document.exitFullscreen) {
        void document.exitFullscreen();
      }
      setIsFullscreen(false);
      return;
    }

    if (cardRef.current?.requestFullscreen) {
      void cardRef.current.requestFullscreen();
    }
    setIsFullscreen(true);
  };

  const showOverlay = Boolean(incomingCall || activeCall);
  const callerLabel = useMemo(
    () => getCallerLabel(incomingCall),
    [incomingCall],
  );
  const callerAvatar = useMemo(
    () => getCallerAvatar(incomingCall),
    [incomingCall],
  );
  const callerInitial = useMemo(
    () => (callerLabel ? callerLabel.charAt(0).toUpperCase() : "?"),
    [callerLabel],
  );
  const localUserId = user?.id || user?._id || null;
  const { localParticipant, remoteParticipant } = useMemo(() => {
    const caller = activeCall?.caller || null;
    const callee = activeCall?.callee || null;
    if (!caller || !callee) {
      return { localParticipant: null, remoteParticipant: null };
    }

    if (localUserId && caller?.id === localUserId) {
      return { localParticipant: caller, remoteParticipant: callee };
    }

    if (localUserId && callee?.id === localUserId) {
      return { localParticipant: callee, remoteParticipant: caller };
    }

    return { localParticipant: caller, remoteParticipant: callee };
  }, [activeCall, localUserId]);
  const localLabel = useMemo(
    () => getParticipantLabel(localParticipant, "You"),
    [localParticipant],
  );
  const remoteLabel = useMemo(
    () => getParticipantLabel(remoteParticipant, "Guest"),
    [remoteParticipant],
  );
  const localAvatar = useMemo(
    () => getParticipantAvatar(localParticipant),
    [localParticipant],
  );
  const remoteAvatar = useMemo(
    () => getParticipantAvatar(remoteParticipant),
    [remoteParticipant],
  );
  const localInitial = useMemo(
    () => (localLabel ? localLabel.charAt(0).toUpperCase() : "?"),
    [localLabel],
  );
  const remoteInitial = useMemo(
    () => (remoteLabel ? remoteLabel.charAt(0).toUpperCase() : "?"),
    [remoteLabel],
  );

  if (!showOverlay || !portalRoot) return null;

  return createPortal(
    <div className={cls.overlay}>
      <div
        ref={cardRef}
        className={`${cls.card} ${isFullscreen ? cls.cardFullscreen : ""}`}
      >
        {incomingCall && !activeCall && (
          <>
            <h3 className={cls.title}>Incoming call</h3>
            <div className={cls.callerInfo}>
              <div className={cls.avatarWrap}>
                {callerAvatar ? (
                  <img
                    src={callerAvatar}
                    alt={`${callerLabel} avatar`}
                    className={cls.avatar}
                  />
                ) : (
                  <span className={cls.avatarFallback}>{callerInitial}</span>
                )}
              </div>
              <p className={cls.subtitle}>@{callerLabel}</p>
            </div>
            <div className={cls.actionsRow}>
              <button
                type="button"
                className={cls.btnPrimary}
                onClick={answerCall}
                disabled={isConnecting}
              >
                Answer
              </button>
              <button
                type="button"
                className={cls.btnGhost}
                onClick={declineCall}
                disabled={isConnecting}
              >
                Decline
              </button>
            </div>
          </>
        )}

        {activeCall && (
          <>
            <div
              className={`${cls.callHeader} ${
                isFullscreen ? cls.callHeaderFullscreen : ""
              }`}
            >
              <div>
                <p className={cls.title}>Call in progress</p>
                <p className={cls.subtitle}>Status: {callStatus}</p>
              </div>
              <div className={cls.headerActions}>
                <button
                  type="button"
                  className={cls.btnGhost}
                  onClick={toggleFullscreen}
                >
                  {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
                </button>
                <button
                  type="button"
                  className={cls.btnDanger}
                  onClick={endCall}
                >
                  End
                </button>
              </div>
            </div>

            <div
              className={`${cls.videoGrid} ${
                isFullscreen ? cls.videoGridFullscreen : ""
              }`}
            >
              <div
                className={`${cls.videoTile} ${
                  isFullscreen ? cls.localThumb : ""
                }`}
              >
                <div ref={localVideoRef} className={cls.videoFrame} />
                <div className={cls.videoMeta}>
                  <div className={cls.videoAvatarWrap}>
                    {localAvatar ? (
                      <img
                        src={localAvatar}
                        alt={`${localLabel} avatar`}
                        className={cls.videoAvatar}
                      />
                    ) : (
                      <span className={cls.videoAvatarFallback}>
                        {localInitial}
                      </span>
                    )}
                  </div>
                  <span className={cls.videoName}>{localLabel}</span>
                </div>
              </div>
              <div
                className={`${cls.videoTile} ${
                  isFullscreen ? cls.remoteFull : ""
                }`}
              >
                <div ref={remoteVideoRef} className={cls.videoFrame} />
                <div className={cls.videoMeta}>
                  <div className={cls.videoAvatarWrap}>
                    {remoteAvatar ? (
                      <img
                        src={remoteAvatar}
                        alt={`${remoteLabel} avatar`}
                        className={cls.videoAvatar}
                      />
                    ) : (
                      <span className={cls.videoAvatarFallback}>
                        {remoteInitial}
                      </span>
                    )}
                  </div>
                  <span className={cls.videoName}>{remoteLabel}</span>
                </div>
              </div>
            </div>

            <div
              className={`${cls.controlsRow} ${
                isFullscreen ? cls.controlsRowFullscreen : ""
              }`}
            >
              <button
                type="button"
                className={cls.iconButton}
                onClick={toggleMute}
                aria-label={isMuted ? "Unmute microphone" : "Mute microphone"}
                title={isMuted ? "Unmute" : "Mute"}
              >
                <img
                  src={isMuted ? unmuteIcon : muteIcon}
                  alt=""
                  className={cls.iconImage}
                />
              </button>
              {localTracks.video && (
                <>
                  <button
                    type="button"
                    className={cls.iconButton}
                    onClick={toggleCamera}
                    aria-label={
                      isCameraOff ? "Turn camera on" : "Turn camera off"
                    }
                    title={isCameraOff ? "Camera On" : "Camera Off"}
                  >
                    <img
                      src={isCameraOff ? cameraOnIcon : cameraOffIcon}
                      alt=""
                      className={cls.iconImage}
                    />
                  </button>
                  <button
                    type="button"
                    className={cls.iconButton}
                    onClick={switchCamera}
                    aria-label="Switch camera"
                    title="Switch Camera"
                  >
                    <img
                      src={switchCameraIcon}
                      alt=""
                      className={cls.iconImage}
                    />
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    portalRoot,
  );
}
